/**
 * interactiveMapLayers.ts — shared registry of "interactive" map layers (T-179 amendment)
 *
 * Several layers own their own click behavior: the POI icon/count-chip/cluster layers
 * (T-179) open a popup or zoom-expand, and the EV charger dots + clusters open a station
 * popup / expand. The site-pin drop logic (PinMarker) also listens for a GLOBAL map click
 * to drop/move the pin. Without a guard, one click does both — clicking a POI moves the
 * pin (the bug Ricardo hit).
 *
 * This module is the single place that knows which layers are interactive. PinMarker asks
 * `isInteractiveFeatureClick` and early-returns when a click lands on one, so the pin only
 * drops on empty map / tract fill. A new interactive layer just adds its id prefix here —
 * the pin-drop logic never needs touching again. We intentionally use queryRenderedFeatures
 * (hit-testing) rather than event ordering or stopPropagation between MapLibre handlers,
 * which are fragile.
 */
import type { Map as MaplibreMap, PointLike } from 'maplibre-gl'

// Layer-id prefixes whose features are interactive. `startsWith` matches both the dynamic
// per-category POI layers (`poi-layer-<slug>`) and the fixed EV charger layer ids.
export const INTERACTIVE_LAYER_ID_PREFIXES = [
  'poi-layer-', // T-179 per-category POI icon + cluster layers
  'ev-chargers-circle', // EV charger points
  'ev-clusters', // EV charger clusters
  'ev-cluster-count', // EV charger cluster count labels
]

/** Interactive layer ids that actually exist on the map's current style. */
export function interactiveLayerIdsOnMap(map: MaplibreMap): string[] {
  let layers: Array<{ id: string }> | undefined
  try {
    layers = map.getStyle()?.layers as Array<{ id: string }> | undefined
  } catch {
    return []
  }
  if (!layers) return []
  return layers
    .map((l) => l.id)
    .filter((id) => INTERACTIVE_LAYER_ID_PREFIXES.some((p) => id.startsWith(p)))
}

/**
 * True when the click at `point` (screen px) landed on an interactive feature — that
 * feature's own handler takes the click, so callers (PinMarker) must not also act on it.
 * Restricts the hit-test to the interactive layers present on the map; false (no query)
 * when none exist.
 */
export function isInteractiveFeatureClick(map: MaplibreMap, point: PointLike): boolean {
  const layers = interactiveLayerIdsOnMap(map)
  if (layers.length === 0) return false
  try {
    return map.queryRenderedFeatures(point, { layers }).length > 0
  } catch {
    return false
  }
}
